/**
 * Calendar invite helpers — generate .ics file content + Google/Outlook URLs.
 *
 * The same event can be added to:
 *   - Apple Calendar / iCal / Outlook desktop / any RFC 5545 client → .ics download
 *   - Google Calendar → URL with template params
 *   - Outlook Web (Office 365) → outlook.office.com/calendar/0/deeplink/compose
 *
 * Pass an event object: { title, description, location, start, end, url }
 * (start/end are JS Date objects or ISO strings; if no end given, +1h is used).
 */

function toIcsDate(d) {
  // .ics expects YYYYMMDDTHHMMSSZ in UTC
  const dt = d instanceof Date ? d : new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    dt.getUTCFullYear().toString() +
    pad(dt.getUTCMonth() + 1) +
    pad(dt.getUTCDate()) + "T" +
    pad(dt.getUTCHours()) +
    pad(dt.getUTCMinutes()) +
    pad(dt.getUTCSeconds()) + "Z"
  );
}

function escapeIcs(s = "") {
  return String(s)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/** Build full RFC 5545 .ics document text. */
export function buildIcs({ title, description, location, start, end, url, organizer = "Ambedkarite Buddhist Global Network UK" }) {
  const startD = start instanceof Date ? start : new Date(start);
  const endD = end ? (end instanceof Date ? end : new Date(end)) : new Date(startD.getTime() + 60 * 60 * 1000);
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}@ambedkaritebuddhistglobalnetworkuk.com`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ABGN-UK//Ambedkarite Buddhist Global Network UK//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toIcsDate(new Date())}`,
    `DTSTART:${toIcsDate(startD)}`,
    `DTEND:${toIcsDate(endD)}`,
    `SUMMARY:${escapeIcs(title)}`,
    description ? `DESCRIPTION:${escapeIcs(description)}` : "",
    location ? `LOCATION:${escapeIcs(location)}` : "",
    url ? `URL:${escapeIcs(url)}` : "",
    `ORGANIZER;CN=${escapeIcs(organizer)}:mailto:info@ambedkaritebuddhistglobalnetworkuk.com`,
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "BEGIN:VALARM",
    "TRIGGER:-PT30M",
    "ACTION:DISPLAY",
    `DESCRIPTION:Reminder: ${escapeIcs(title)}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ].filter(Boolean);
  return lines.join("\r\n");
}

/** Trigger a browser download of the .ics file for a given event. */
export function downloadIcs(event) {
  const ics = buildIcs(event);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeTitle = (event.title || "event").replace(/[^a-z0-9-]+/gi, "_").slice(0, 60);
  a.href = u;
  a.download = `${safeTitle}.ics`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(u), 5000);
}

/** Google Calendar URL. */
export function googleCalUrl({ title, description, location, start, end, url }) {
  const startD = start instanceof Date ? start : new Date(start);
  const endD = end ? (end instanceof Date ? end : new Date(end)) : new Date(startD.getTime() + 60 * 60 * 1000);
  const fmt = (d) =>
    d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "",
    details: [description, url].filter(Boolean).join("\n\n"),
    location: location || "",
    dates: `${fmt(startD)}/${fmt(endD)}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Outlook Web (Office 365) URL. */
export function outlookCalUrl({ title, description, location, start, end, url }) {
  const startD = start instanceof Date ? start : new Date(start);
  const endD = end ? (end instanceof Date ? end : new Date(end)) : new Date(startD.getTime() + 60 * 60 * 1000);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: title || "",
    body: [description, url].filter(Boolean).join("\n\n"),
    location: location || "",
    startdt: startD.toISOString(),
    enddt: endD.toISOString(),
  });
  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
}
